import { Check } from 'lucide-react'
import type { HouseholdMember, UserId } from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { Dialog } from '@components/Dialog.tsx'
import { initialFor, memberLabel } from '@utils/members.ts'
import styles from './AssigneePicker.module.css'

type Props = {
	open: boolean
	members: HouseholdMember[]
	selected: UserId[]
	onToggle: (id: UserId) => void
	onClose: () => void
}

// Multi-select list of household members. Controlled: each tap calls onToggle
// and the parent owns the selection (draft state in the composer, or an
// optimistic update on a card).
export const AssigneePicker = ({
	open,
	members,
	selected,
	onToggle,
	onClose,
}: Props) => {
	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				<h2 className={styles.heading}>Assign to</h2>

				{members.length === 0 ? (
					<p className={styles.empty}>No household members yet.</p>
				) : (
					<ul className={styles.list}>
						{members.map((m) => {
							const on = selected.includes(m.id)
							return (
								<li key={m.id}>
									<button
										type="button"
										className={`${styles.item} ${on ? styles.selected : ''}`.trim()}
										aria-pressed={on}
										onClick={() => onToggle(m.id)}
									>
										<span
											className={styles.avatar}
											style={{
												background: colorFor(m.id),
											}}
											aria-hidden="true"
										>
											{initialFor(m)}
										</span>
										<span className={styles.name}>
											{memberLabel(m)}
										</span>
										{on && (
											<Check
												className={styles.check}
												size={18}
												aria-hidden="true"
											/>
										)}
									</button>
								</li>
							)
						})}
					</ul>
				)}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.done}
						onClick={onClose}
					>
						Done
					</button>
				</div>
			</div>
		</Dialog>
	)
}
